import { DISPATCH_WINDOW } from "./hardware";

// Support Angle Thickness — a real Shopify variant option on the Supported
// Slide-On Bracket product. Both brackets share the SAME 2.0 mm steel sleeve;
// the only difference is the Duragal angle thickness:
//   · Standard   — 2.5 mm Duragal angle, $7.50 (variant "2.5 mm"). The entry
//                  Australian-made spec for the vast majority of jobs.
//   · Heavy Duty — 3.0 mm Duragal angle, $7.95 (variant "3.0 mm"). The premium
//                  upgrade — a thicker angle for increased strength and
//                  long-term durability.
// Each thickness is its own fully priced Shopify variant, so there is no
// separate per-bracket upgrade line — see lib/shopify.js. Copy describes
// material thickness only (no structural/load claims).

// Retained at 0 so any remaining upgrade-aware maths is a no-op. Each angle's
// price is carried by its own variant/tier table now, not by a surcharge.
export const ANGLE_UPGRADE_PRICE = 0;

export const SUPPORT_ANGLES = [
  {
    id: "standard",
    name: "2.5 mm Duragal Angle",
    short: "2.5 mm",
    thickness: "2.5 mm",
    upgrade: 0,
    priceHint: "$7.50 ea",
    desc: "2.5 mm Duragal angle on a 2.0 mm steel sleeve. Designed for the vast majority of fencing and landscaping applications.",
    skuSuffix: "25MM",
  },
  {
    // Heavy Duty 3.0 mm — TEMPORARILY NOT OFFERED (can't source 3.0 mm Duragal
    // stock right now, 2026-07-26). Definition + HEAVY_TIERS retained so it can
    // be re-enabled by adding "heavy" back to SUPPORTED_ANGLE_STOCK below and
    // flipping the Shopify "3.0 mm" variant (46934713073718) back to CONTINUE.
    id: "heavy",
    name: "Heavy Duty — 3.0 mm Duragal Angle",
    short: "3.0 mm",
    thickness: "3.0 mm",
    upgrade: 0,
    priceHint: "$7.95 ea",
    desc: "A thicker 3.0 mm Duragal angle on the same 2.0 mm steel sleeve, for increased strength and long-term durability.",
    skuSuffix: "30MM",
  },
  {
    // Flat Slide-On Bracket only — a 3.0 mm galvanised flat angle (no lip).
    id: "threemm",
    name: "3.0 mm Flat Steel",
    short: "3.0 mm",
    thickness: "3.0 mm",
    upgrade: 0,
    desc: "The current standard specification for the Flat Slide-On Bracket.",
    skuSuffix: "30MMFLAT",
  },
];

export const ANGLE_BY_ID = Object.fromEntries(SUPPORT_ANGLES.map((a) => [a.id, a]));

export const ANGLE_HELPER =
  "Our 2.5 mm Duragal support angle on a 2.0 mm steel sleeve — designed for the vast majority of fencing and landscaping applications.";

export const ANGLE_COMPONENT_NOTE =
  "The 2.5 mm measurement refers to the Duragal support angle; the steel sleeve is 2.0 mm.";

// Per-page availability + default selection.
// Status values: "in" (in stock — shows the buy-now tag and DISPATCH_WINDOW),
// "preorder" (buyable but made to order — shows the pre-order notice and its
// 2–3 week window), "out" (unavailable, not buyable).
// Supported: 2.5 mm Duragal is the only offered angle right now (Heavy Duty
// 3.0 mm is temporarily not sourced — omit "heavy" here to hide it from the
// selector; add it back when 3.0 mm stock is available).
// Flat: 3.0 mm flat angle is its only spec.
// Flip either value back to "preorder" to restore the pre-order messaging
// site-wide, and re-add the thickness to PREORDER_ANGLE_THICKNESSES below.
export const SUPPORTED_ANGLE_STOCK = { standard: "in", default: "standard" };
export const FLAT_ANGLE_STOCK = { threemm: "in", default: "threemm" };

// In-stock tag shown in place of the pre-order notice. Wording comes from the
// single DISPATCH_WINDOW source in data/hardware.js so the tag, the shipping
// line and the schema handling time can never quote different numbers.
export const STOCK_TAG_TITLE = "IN STOCK — BUY NOW";
export const stockTagBody = (dispatchWindow = DISPATCH_WINDOW) =>
  `Orders are usually dispatched within ${dispatchWindow}. Tracking is provided as soon as your ` +
  `order leaves our workshop.`;

// ----------------------------------------------------------------------------
// Pre-order / unavailable messaging. Kept here so the product pages, the cart
// drawer and the home buy widget all show identical, consistent copy.
//
// Both supported angles (2.5 mm and 3.0 mm) and the 3.0 mm flat angle are on
// pre-order — the variants that must have Shopify "Continue selling when out of
// stock" enabled.
// ----------------------------------------------------------------------------
export const PREORDER_LABEL = "PRE-ORDER NOW";

export const PREORDER_NOTICE_TITLE = "CURRENTLY AVAILABLE FOR PRE-ORDER";

// specLabel keeps the wording accurate per selection (e.g. "Standard 2.5 mm" vs
// "Heavy Duty 3.0 mm" vs "3.0 mm flat") while the rest of the message stays identical.
export const preorderNoticeBody = (specLabel = "Australian-made") =>
  `Due to stronger-than-expected demand, our ${specLabel} brackets are currently being produced. ` +
  `Orders placed now are estimated to dispatch within 2–3 weeks. Tracking will be provided as soon as ` +
  `your order leaves our workshop.`;

// Retained for completeness — no support angle is currently unavailable, so this
// notice does not render. Kept generic (no discontinued spec named) in case a
// future variant is taken offline.
export const UNAVAILABLE_NOTICE_TITLE = "CURRENTLY UNAVAILABLE";
export const UNAVAILABLE_NOTICE_BODY =
  "This option is currently unavailable. Please select an available support angle to continue.";

// Support-angle thickness values (as stored on the Shopify cart line "Support
// angle" attribute) that represent a pre-order spec. Used to decide whether the
// cart pre-order notice should show. EMPTY while everything is in stock — add a
// thickness back here if that spec goes to pre-order again, otherwise the cart
// would keep warning about a pre-order the product pages no longer mention.
export const PREORDER_ANGLE_THICKNESSES = new Set();
